import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Icon,
  InlineStack,
  Link,
  Panel,
  PanelSection,
  Spinner,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  Tooltip,
  Truncated,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import plur from 'plur';
import { channelValueOf, getDeliveryChannel } from '@/features/deliveries';
import type { Delivery } from '@/services/db/delivery/entity';
import { useRelatedDeliveries } from '@/services/db/delivery/queries';
import { getDeviceStatuses, type PushDeliveryMetaReason } from '@/services/db/delivery/utils';
import { isEmptyString } from '@/utils/string';

type RelatedDeliveriesProps = {
  delivery: Delivery;
  /** Builds a related row's href — drafts route to Ember, sent to the React detail. */
  getDeliveryHref: (delivery: Delivery) => string;
};

/**
 * The resend-chain panel: the delivery's `related_deliveries` siblings, each
 * read by id and rendered as a row. The linkage ids are known up front, so the
 * section and its count render immediately with a spinner while the siblings
 * resolve. A single sibling expands by default; multiple collapse.
 */
export function RelatedDeliveries({ delivery, getDeliveryHref }: RelatedDeliveriesProps) {
  const relatedIds = delivery.related_deliveries ?? [];
  const { deliveries, isLoading } = useRelatedDeliveries(relatedIds);

  if (relatedIds.length === 0) {
    return null;
  }

  return (
    <Panel data-test-related-deliveries>
      <PanelSection withPadding={false} withBorder>
        <Accordion defaultOpen={relatedIds.length === 1}>
          <AccordionTitle px="250" py="125" data-test-related-deliveries-title>
            <Text size="sm" weight="regular">
              {relatedIds.length} related {plur('message', relatedIds.length)}
            </Text>
          </AccordionTitle>

          <AccordionContent p="0" pb="025">
            {isLoading ? (
              <InlineStack px="250" py="125" data-test-related-deliveries-loading>
                <Spinner shouldShowLoadingText />
              </InlineStack>
            ) : (
              <RelatedDeliveriesTable deliveries={deliveries} getDeliveryHref={getDeliveryHref} />
            )}
          </AccordionContent>
        </Accordion>
      </PanelSection>
    </Panel>
  );
}

type RelatedDeliveriesTableProps = {
  deliveries: readonly Delivery[];
  getDeliveryHref: (delivery: Delivery) => string;
};

function RelatedDeliveriesTable({ deliveries, getDeliveryHref }: RelatedDeliveriesTableProps) {
  return (
    <Table caption="List of related deliveries" isBorderless>
      <TableThead>
        <TableTr>
          <TableTh aria-hidden h="0" p="0" style={{ width: '1%', whiteSpace: 'nowrap' }} />
          <TableTh aria-hidden h="0" p="0" style={{ maxWidth: 0, width: '50%' }} />
          <TableTh aria-hidden h="0" p="0" style={{ maxWidth: 0, width: '50%' }} />
        </TableTr>
      </TableThead>

      <TableTbody>
        {deliveries.map((related) => (
          <RelatedDeliveryRow key={related.id} delivery={related} getDeliveryHref={getDeliveryHref} />
        ))}
      </TableTbody>
    </Table>
  );
}

type RelatedDeliveryRowProps = {
  delivery: Delivery;
  getDeliveryHref: (delivery: Delivery) => string;
};

function RelatedDeliveryRow({ delivery, getDeliveryHref }: RelatedDeliveryRowProps) {
  const channelValue = channelValueOf(delivery.action_type);
  const channel = getDeliveryChannel(channelValue);
  const subject = isEmptyString(delivery.subject) ? '(No subject)' : delivery.subject;
  const device = getDeviceStatuses(delivery)[0];

  return (
    <TableTr data-test-related-delivery={delivery.id}>
      <TableTd data-test-related-delivery-type>
        <InlineStack alignBlock="center">
          <Tooltip content={channel?.name ?? channelValue} placement="top">
            <Icon name={channel?.icon ?? 'help'} size="sm" display="block" />
          </Tooltip>
        </InlineStack>
      </TableTd>

      <TableTd style={{ maxWidth: 0 }}>
        <InlineStack alignBlock="center" minW="0">
          {/* A plain link handles both destinations, same as the Outbox list cell: the router
              transitions to the React detail for a sent sibling and hands a drafted sibling off
              to the Ember-owned draft route. */}
          <Link href={getDeliveryHref(delivery)} truncate className="fs-mask" data-test-related-delivery-title>
            {subject}
          </Link>
        </InlineStack>
      </TableTd>

      <TableTd style={{ maxWidth: 0 }} data-test-related-delivery-device>
        {device == null ? null : <DeviceCell device={device} />}
      </TableTd>
    </TableTr>
  );
}

const PLATFORM_ICONS: Record<string, IconName> = { web: 'desktop', ios: 'apple', android: 'android' };
const PLATFORM_LABELS: Record<string, string> = { web: 'Web', ios: 'iOS', android: 'Android' };

type DeviceCellProps = { device: PushDeliveryMetaReason };

/**
 * A related push delivery targets a single device; show its platform icon and
 * token. The app-name suffix (Ember `appName`, resolved from the loaded apps
 * list) is deferred with the rest of push app resolution (FPS-3178).
 */
function DeviceCell({ device }: DeviceCellProps) {
  const icon = PLATFORM_ICONS[device.platform] ?? 'desktop';
  const label = PLATFORM_LABELS[device.platform] ?? device.platform;

  return (
    <InlineStack alignBlock="center" gap="050" minW="0">
      <Tooltip content={label} placement="bottom">
        <Icon name={icon} color="subtle" flexShrink="0" data-test-push-platform-icon />
      </Tooltip>
      <Truncated tooltip={device.id} family="mono" truncate size="xs" className="fs-mask">
        {device.id}
      </Truncated>
    </InlineStack>
  );
}
